import { installLanguage } from '@/i18n';
import '@/i18n/language.css';
import LanguageSwal from 'sweetalert2';
import StandaloneLanguageSwal from 'sweetalert2/dist/sweetalert2.js';
import { createApp } from "vue";
import { createWebHistory, createRouter } from "vue-router";
import { mask } from "vue-the-mask";
import axios from "axios";

// styles
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@/assets/styles/tailwind.css";
import "@/assets/styles/mobile.css";

// mounting point
import App from "@/App.vue";

// layouts
import Admin from "@/layouts/Admin.vue";
import Auth from "@/layouts/Auth.vue";

// views for Admin layout
const Dashboard = () => import(/* webpackChunkName: "client-dashboard" */ "@/views/main/Dashboard.vue");
const Message = () => import(/* webpackChunkName: "client-message" */ "@/views/main/Message.vue");
const change_password = () => import(/* webpackChunkName: "client-change-password" */ "@/views/main/change_password.vue");
const AccountSecurity = () => import(/* webpackChunkName: "client-account-security" */ "@/views/main/AccountSecurity.vue");
const Tables = () => import(/* webpackChunkName: "client-tables" */ "@/views/main/Tables.vue");
const Maps = () => import(/* webpackChunkName: "client-maps" */ "@/views/main/Maps.vue");
const Employees = () => import(/* webpackChunkName: "client-employees" */ "@/views/main/Employees.vue");
const Payables = () => import(/* webpackChunkName: "client-payables" */ "@/views/main/Payables.vue");
const Payroll = () => import(/* webpackChunkName: "client-payroll" */ "@/views/main/Payroll.vue");
const TimeSheet = () => import(/* webpackChunkName: "client-time-sheet" */ "@/views/main/Timesheet.vue");
const Resources = () => import(/* webpackChunkName: "client-resources" */ "@/views/main/Resources.vue");
const ResourcesSubmission = () => import(/* webpackChunkName: "client-resources-submission" */ "@/views/main/file_uploader.vue");
const Reports = () => import(/* webpackChunkName: "client-reports" */ "@/views/main/Reports.vue");
const VendorInvoice = () => import(/* webpackChunkName: "client-vendor-invoice" */ "@/views/main/VendorInvoice.vue");
const EmployeeInvoices = () => import(/* webpackChunkName: "client-employee-invoices" */ "@/views/main/EmployeeInvoices.vue");
const VendorInvoiceUpload = () => import(/* webpackChunkName: "client-vendor-invoice-upload" */ "@/views/main/VendorInvoiceUpload.vue");
const BudgetMonitoring = () => import(/* webpackChunkName: "client-budget-monitoring" */ "@/views/main/BudgetMonitoring.vue");
const Enrollment = () => import(/* webpackChunkName: "client-enrollment" */ "@/views/main/Enrollment.vue");
const HireEmployees = () => import(/* webpackChunkName: "client-hire-employees" */ "@/views/main/hired_employees.vue");
const VendorHiring = () => import(/* webpackChunkName: "client-vendor-hiring" */ "@/views/main/Vendors.vue");
const Signature = () => import(/* webpackChunkName: "client-signature" */ "@/views/main/Signature.vue");
const PurchaseOrders = () => import(/* webpackChunkName: "client-purchase-orders" */ "@/views/main/PurchaseOrders.vue");
const SpendingHistory = () => import(/* webpackChunkName: "client-spending-history" */ "@/views/main/SpendingHistory.vue");
const EmployeeEnrollmentWizard = () => import(/* webpackChunkName: "client-employee-enrollment-wizard" */ "@/views/main/EmployeeEnrollmentWizard.vue");

// views for Auth layout
const Login = () => import(/* webpackChunkName: "client-login" */ "@/views/auth/Login.vue");
const Register = () => import(/* webpackChunkName: "client-register" */ "@/views/auth/Register.vue");
const forgot_password = () => import(/* webpackChunkName: "client-forgot-password" */ "@/views/auth/forgot_password.vue");
const VendorSignUp = () => import(/* webpackChunkName: "client-vendor-sign-up" */ "@/views/auth/VendorSignUp.vue");

// views without layouts
const Landing = () => import(/* webpackChunkName: "client-landing" */ "@/views/Landing.vue");
const Profile = () => import(/* webpackChunkName: "client-profile" */ "@/views/Profile.vue");

const routes = [
  {
    path: "/admin",
    redirect: "/main/dashboard",
    component: Admin,
    children: [
      { path: '/main/document-templates/:templateId', name: 'Personal Document Editor', component: () => import(/* webpackChunkName: "client-document-editor" */ '@/views/PortalDocumentEditor.vue'), props: route => ({ templateId: route.params.templateId, portal: 'client', backPath: '/main/resources' }), meta: { requiresAuth: true } },
      {
        path: "/main/spending-history",
        component: SpendingHistory,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/dashboard",
        component: Dashboard,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/message",
        component: Message,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/resources",
        component: Resources,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/reports",
        component: Reports,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/purchase-order",
        component: PurchaseOrders,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendors-hiring",
        component: VendorHiring,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/hire_employees",
        component: HireEmployees,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/change_password",
        component: change_password,
        meta: { requiresAuth: true },
      },
      { path: "/main/device-management", component: AccountSecurity, meta: { requiresAuth: true } },
      { path: "/main/account-settings", component: AccountSecurity, props: { tab: 'settings' }, meta: { requiresAuth: true } },
      {
        path: "/main/budget_monitoring",
        component: BudgetMonitoring,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/enrollment",
        component: Enrollment,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/signature",
        component: Signature,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/tables",
        component: Tables,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/maps",
        component: Maps,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/employees",
        component: Employees,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/payables",
        component: Payables,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/Resources_uploader",
        component: ResourcesSubmission,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/timesheets",
        component: TimeSheet,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/payroll",
        component: Payroll,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendor_invoice",
        component: VendorInvoice,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/employee-invoices",
        component: EmployeeInvoices,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/vendor_invoice_upload",
        component: VendorInvoiceUpload,
        meta: { requiresAuth: true },
      },
      {
        path: "/main/employee-enrollment",
        component: EmployeeEnrollmentWizard,
        meta: { requiresAuth: true },
      },
    ],
  },
  {
    path: "/auth",
    redirect: "/auth/login",
    component: Auth,
    children: [
      {
        path: "/auth/login",
        component: Login,
      },
      {
        path: "/auth/register",
        component: Register,
      },
      {
        path: "/auth/forgot_password",
        component: forgot_password,
      },
      {
        path: "/sign-up",
        component: VendorSignUp,
      },
    ],
  },
  {
    path: "/landing",
    component: Landing,
  },
  {
    path: "/profile",
    component: Profile,
  },
  {
    path: "/",
    redirect: "/auth/login",
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/auth/login",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function clearClientPortalSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenExpiration");
  localStorage.removeItem("portal");
  localStorage.removeItem("user_type");
}

function isPublicRoute(path) {
  return (
    path === "/auth/login" ||
    path === "/auth/register" ||
    path === "/auth/forgot_password" ||
    path === "/sign-up"
  );
}

function isTokenValid(token, tokenExpiration) {
  if (!token || !tokenExpiration) return false;

  const expiration = Number(tokenExpiration);
  if (!Number.isFinite(expiration)) return false;

  return Date.now() < expiration;
}

function isVendorUser() {
  return localStorage.getItem("user_type") === "vendor";
}

function homeRouteForCurrentUser() {
  return isVendorUser() ? "/main/vendor_invoice_upload" : "/main/dashboard";
}

// Vendors only get their own invoice page + change password; everything else
// in /main/* is client/coordinator-only.
function isVendorAllowedRoute(path) {
  return (
    path === "/main/vendor_invoice_upload" ||
    path === "/main/change_password"
  );
}

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem("token");
  const tokenExpiration = localStorage.getItem("tokenExpiration");
  const publicRoute = isPublicRoute(to.path);
  const validToken = isTokenValid(token, tokenExpiration);

  if (validToken && !isVendorUser() && localStorage.getItem("user_type") !== "coordinator" && (!publicRoute || to.path === "/auth/login")) {
    try {
      await axios.get(`${process.env.VUE_APP_BACKEND_DOMAIN}/api/client-session`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
    } catch (error) {
      if (localStorage.getItem("token") !== token) return next(false);
      if (error.response?.status === 401 || error.response?.status === 403) {
        clearClientPortalSession();
        return next({ path: "/auth/login", query: { reason: error.response.status === 403 ? "account-inactive" : "session-expired" }, replace: true });
      }
      // A temporary connection failure must not erase a valid login.
    }
    if (localStorage.getItem("token") !== token) return next(false);
  }

  // Public auth pages should always load.
  if (publicRoute) {
    // Optional: if a valid session exists and user manually opens /auth/login,
    // send them to their role's landing page instead.
    if (validToken && to.path === "/auth/login") {
      return next(homeRouteForCurrentUser());
    }
    return next();
  }

  // Any protected page requires a valid token.
  if (validToken) {
    // Extend session on active use.
    const newExpirationTime = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem("tokenExpiration", String(newExpirationTime));

    if (isVendorUser() && !isVendorAllowedRoute(to.path)) {
      return next("/main/vendor_invoice_upload");
    }

    return next();
  }

  // Stale or missing session: clear it and go to login.
  clearClientPortalSession();
  return next("/auth/login");
});

axios.interceptors.response.use(response => response, error => {
  const request = error.config;
  const token = localStorage.getItem("token");
  const authorization = request?.headers?.get?.("Authorization") || request?.headers?.Authorization;
  if (error.response?.status === 401 && token && authorization === `Bearer ${token}` && request?.url?.startsWith(`${process.env.VUE_APP_BACKEND_DOMAIN}/api/`) && !request.url.endsWith("/client-session")) {
    clearClientPortalSession();
    router.replace({ path: "/auth/login", query: { reason: "session-expired" } });
  }
  return Promise.reject(error);
});

window.addEventListener("storage", event => {
  if (event.key === "token" && event.oldValue !== event.newValue) window.location.reload();
});

const app = createApp(App);
installLanguage(app, LanguageSwal, { portal: 'client', router });
if (StandaloneLanguageSwal !== LanguageSwal) installLanguage(app, StandaloneLanguageSwal);
app.directive("mask", mask);
app.use(router);
router.isReady().then(() => app.mount("#app"));
